import { isAxiosError } from 'axios';
import { APPX_ORG_API_URL, USER_SERVICE_URL } from '~/modules/config';
import { getPreferredLanguage } from '~/modules/helpers/languageHelper';
import { TpalInfo, UserPerm } from '~/modules/stores/userStore';
import { handleException } from '~/modules/utilities/error_utilities';
import { HttpRequestConfig, http } from '~/modules/utilities/http';
import { setupOrgRequestConfig, setupTMPrivateRequestConfig } from '~/modules/utilities/httpUtilities';
import { logger } from '~/modules/utilities/logger';
import { isValidSalesforceOrgId } from '~/modules/utilities/salesforce_id';
import { UserTokenService } from '../services/userTokenService';

export interface UserContext {
    orgid: string;
    orgstatus: string;
    orgedition: string;
    topCategories: string[];
    mainCategory: string;
    products: string[];
    region: string;
    industry: string;
    userRole: string;
    userLinkedOrgs: string[];
    lastInstalledListings: string[];
}

export type OrgEdition = 'Production' | 'Sandbox' | 'Developer Edition' | 'Scratch Org' | 'Dev Hub' | 'Unknown';

/** A user's org as surfaced to the org picker UI (matches the fields the picker renders). */
export interface OrgInfo {
    orgId: string;
    tenantKey: string;
    name: string;
    username: string;
    domain: string;
    instanceId: string;
    edition: OrgEdition;
}

/**
 * The org list plus the server-provided total count. `totalCount` is the count the
 * user-context service reports — NOT derived from `orgs.length`.
 */
export interface TenantsResult {
    orgs: OrgInfo[];
    totalCount: number;
}

/** Thrown when the tenant fetch fails for any reason. The caller treats every failure the same —
 *  there is no retry, so no need to distinguish a timeout from any other error. `cause` carries the
 *  original error through for logging without changing that caller behavior. When `cause` is an
 *  AxiosError, its config is tagged to redact the Authorization header (the sid bearer token from
 *  setupOrgRequestConfig) so JSON.stringify-ing the error for the logger never leaks it. */
export class TenantsError extends Error {
    cause?: unknown;

    constructor(cause?: unknown) {
        super('Failed to load tenants');
        this.name = 'TenantsError';
        if (isAxiosError(cause) && cause.config) {
            cause.config.redact = [...(cause.config.redact ?? []), 'Authorization'];
        }
        this.cause = cause;
    }
}

/** Raw tenant shape returned by the user-context /v1/tenants endpoint. */
interface Tenant {
    orgId: string;
    tenantKey: string;
    tenantName: string;
    username: string;
    domain: string;
    instanceId: string;
    tenantType: string;
}

export interface TenantsResponse {
    tenants: Tenant[];
    totalCount: number;
}

// Mirrors Core's getTenantType() (core/c2c/java/src/c2c/util/C2CTenantSyncUtil.java) - the tenantType
// values GDOT syncs to the user-context service. That method only ever returns PROD, PRE_PROD,
// FREE_EDITION, DEV, or HUB (see its javadoc bucket list): scratch->DEV, hub->HUB, any sandbox flavor
// ->PRE_PROD, free/developer->FREE_EDITION, else PROD. Core collapses every sandbox subtype into
// PRE_PROD and has no playground concept, so those are the only five values we can receive here.
// Anything else falls back to Unknown (and is logged, so a future Core bucket change fails loud).
function toOrgEdition(tenantType: string): OrgEdition {
    switch (tenantType) {
        case 'PROD':
            return 'Production';
        case 'PRE_PROD':
            return 'Sandbox';
        case 'FREE_EDITION':
            return 'Developer Edition';
        case 'DEV':
            return 'Scratch Org';
        case 'HUB':
            return 'Dev Hub';
        default:
            logger.warn(`toOrgEdition: unrecognized tenantType '${tenantType}'`);
            return 'Unknown';
    }
}

// Trims a tenant string field, tolerating a null/undefined/non-string value from a malformed record.
// A single bad field must degrade to '' rather than throwing — one malformed tenant should not
// collapse the entire org list into a TenantsError (WI: Error Handling Fixes).
function safeTrim(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function toOrgInfo(tenant: Tenant): OrgInfo {
    return {
        orgId: tenant.orgId,
        tenantKey: tenant.tenantKey,
        name: safeTrim(tenant.tenantName),
        username: safeTrim(tenant.username),
        domain: safeTrim(tenant.domain),
        instanceId: tenant.instanceId,
        edition: toOrgEdition(tenant.tenantType),
    };
}

export interface UserInfo {
    isGuestUser: boolean;
    userId?: string;
    firstName?: string;
    lastName?: string;
    canInstall?: boolean;
    canCheckout?: boolean;
    tbidProfileUrl?: string;
    userPicture?: string;
    savedListings?: string[];
    userToken: string;
    tokenExpirationDate: number;
    userContext: UserContext;
    recentActivity: string[];
    company: string;
    state: string;
    country: string;
    phone: string;
    email: string;
}

export class UserService {
    get endpoint(): string {
        return USER_SERVICE_URL + '/v1';
    }

    async getUser(bypassCache = false): Promise<UserInfo> {
        const url = await this.buildEndpoint();
        const httpConfig: HttpRequestConfig = setupOrgRequestConfig();

        if (bypassCache) {
            httpConfig.params = { 'bypass-cache': true };
        }

        let userInfo: UserInfo;
        try {
            const response = await http.get(url, httpConfig);
            userInfo = response.data;
        } catch (error) {
            handleException(error, 'userService.getUser');
        }
        // @ts-expect-error * Automated Type Error Fix
        return userInfo;
    }

    private async buildEndpoint() {
        const tokenInfo: AnyObject = await UserTokenService.getTokenRegistrationParams();
        return `${this.endpoint}/user?userToken=${tokenInfo.userToken}&expiresTimestamp=${
            tokenInfo.expirationTimestamp
        }&language=${getPreferredLanguage()}`;
    }

    // @ts-expect-error * Automated Type Error Fix
    getOrganizationId(user) {
        const lastLoggedIn = /salesforce:+(00D[a-zA-Z0-9]{15})/.exec(user.profile?.custom_attributes?.LastLoggedInFrom);
        if (lastLoggedIn) {
            return lastLoggedIn[1];
        }
        // if lastLoggedIn isn't a salesforce tapl check
        const tpalinksCount = user.profile?.custom_attributes?.ThirdPartyAccountLinksNumber || 0;
        for (let i = 1; i <= tpalinksCount; i++) {
            const tpalink = `ThirdPartyAccountLink${i}`;
            const tpalinkValue = user.profile.custom_attributes[tpalink];
            const orgId = /salesforce:+(00D[a-zA-Z0-9]{15})/.exec(tpalinkValue);
            if (orgId) {
                return orgId[1];
            }
        }
        return null;
    }

    async getTpalsUserPerms(tpals: TpalInfo[]): Promise<UserPerm[]> {
        const url = APPX_ORG_API_URL + '/userperm';
        const request: HttpRequestConfig = setupTMPrivateRequestConfig();
        request.data = tpals;
        try {
            const { data } = await http.post(url, request);
            return data;
        } catch (error) {
            handleException(error, 'getTpalsUserPerms');
            return [];
        }
    }

    /**
     * Fetches the orgs (tenants) the authenticated user belongs to from the user-context service,
     * with the service-provided total count. Auth reuses the org request config (sid bearer), same
     * as getUser; the request times out at ORG_API_TIMEOUT. The call is made exactly once — there is
     * no retry: any failure throws a TenantsError and the page sends the user home. A successful
     * empty response is a valid "no orgs" result, not an error.
     */
    async getTenants(): Promise<TenantsResult> {
        const url = `${this.endpoint}/tenants`;

        try {
            const response = await http.get(url, setupOrgRequestConfig());
            const data = response.data as TenantsResponse;
            // Keep only Salesforce Core orgs with a valid 15/18-char `00D` orgId. This drops two kinds
            // of record: (1) malformed Core records with a missing/blank orgId, which map to a blank,
            // unselectable ghost card (org-card needs orgId to dispatch a selection, selectOrg needs it
            // to resolve the click); and (2) non-Core tenants (Slack, Tableau, ...) the multi-product
            // /v1/tenants feed also returns, whose ids don't take the `00D` shape and which the org
            // picker can't act on. Both degrade to "absent" rather than an inert row.
            const usableTenants = data.tenants.filter((tenant) => tenant && isValidSalesforceOrgId(tenant.orgId));
            const orgs = usableTenants.map(toOrgInfo);
            // Decrement totalCount by however many records we dropped so the `orgs.length === totalCount`
            // completeness gate (orgContextHelper, header, selectOrg) still holds — those consumers treat
            // a mismatch as "list not fully loaded" and would otherwise wedge selection validation and
            // stale-org cleanup shut forever on a single malformed record. If the service omits a numeric
            // totalCount, fall back to orgs.length so the gate reads "complete" rather than comparing
            // against NaN (which would wedge it shut permanently).
            const droppedCount = data.tenants.length - usableTenants.length;
            const totalCount = Number.isFinite(data.totalCount) ? data.totalCount - droppedCount : orgs.length;
            return { orgs, totalCount };
        } catch (error) {
            throw new TenantsError(error);
        }
    }
}
